import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NavGroup, navigation } from 'app/trello/components/shared/page-layout/side-navigation';
import { UserPreferenceStore } from 'app/trello/services/user-preference-store';
import { Templating } from 'app/trello/templating.imports';
import gsap from 'gsap';
import { Tooltip } from 'primeng/tooltip';
import { filter } from 'rxjs';

@Component({
  imports: [
    Templating,
    RouterOutlet,
    Tooltip,
    RouterLink,
  ],
  selector: 'app-page-layout',
  templateUrl: 'page-layout.component.html',
  styleUrls: ['./page-layout.component.css'],
})
@UntilDestroy()
export class PageLayoutComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('grid') grid!: ElementRef;
  @ViewChild('logo') logo!: ElementRef;
  @ViewChildren('navLabel') navLabels!: QueryList<ElementRef>;
  @ViewChildren('navGroupTitle') navGroupTitles!: QueryList<ElementRef>;

  protected animating: boolean = false;

  protected readonly navigation: NavGroup[] = navigation;

  protected selectedPage: any = 'home';

  private gridTimeline!: gsap.core.Timeline;
  private logoTimeline!: gsap.core.Timeline;
  private navLabelTimeline!: gsap.core.Timeline;
  private navGroupTitleTimeline!: gsap.core.Timeline;

  private sideExpanded: boolean = true;

  constructor(
    private readonly host: ElementRef,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userPreferenceStore: UserPreferenceStore,
  ) {
  }

  public ngOnInit(): void {

    this.setInitialSideExpandedValue();

    this.subscribeToPageChanges();
  }

  public ngAfterViewInit(): void {
    this.initialiseAnimations();
  }

  public ngOnDestroy(): void {
    this.destroyAnimations();
  }

  protected showSideOptions(): boolean {
    return this.selectedPage !== 'onboarding';
  }

  protected sideCollapsable(): boolean {
    return this.sideExpanded;
  }

  protected toggleSideExpanded() {

    this.animating = true;

    const sideExpanded = !this.sideExpanded;

    this.sideExpanded = sideExpanded;

    this.animateTransition(sideExpanded);

    this.userPreferenceStore.userPrefersSideExpanded = sideExpanded;
  }

  private setInitialSideExpandedValue() {

    this.sideExpanded = this.userPreferenceStore.userPrefersSideExpanded;
  }

  private subscribeToPageChanges(): void {

    const handleRouteData = (data: any) => {

      if (data && data['selectedPage']) {
        this.selectedPage = data['selectedPage'];
      }
    };

    handleRouteData(this.route.firstChild?.snapshot.data);

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        handleRouteData(this.route.firstChild?.snapshot.data);
      });
  }

  private animateTransition(
    expanded: boolean,
  ): void {

    if (expanded) {

      this.gridTimeline.reverse();
      this.logoTimeline.reverse();
      this.navLabelTimeline.reverse();
      this.navGroupTitleTimeline.reverse();

    } else {

      this.gridTimeline.play();
      this.logoTimeline.play();
      this.navLabelTimeline.play();
      this.navGroupTitleTimeline.play();
    }
  }

  private initialiseAnimations(): void {

    this.initialiseGridAnimation();
    this.initialiseLogoAnimation();
    this.initialiseNavLabelAnimations();
    this.initialiseNavGroupTitleAnimations();
  }

  private initialiseGridAnimation(): void {

    if (!this.grid) {
      console.error('Grid element not found');
      return;
    }

    const styles = getComputedStyle(this.host.nativeElement);

    const expandedWidth = styles.getPropertyValue('--side-expanded-width').trim();
    const collapsedWidth = styles.getPropertyValue('--side-collapsed-width').trim();

    gsap.set(this.grid.nativeElement, {'--side-width': expandedWidth});

    this.gridTimeline =
      gsap.timeline({
        paused: true,
        onUpdate: () => {
          this.animating = true;
        },
        onComplete: () => {
          this.animating = false;
        },
        onReverseComplete: () => {
          this.animating = false;
        },
      });

    this.gridTimeline.to(this.grid.nativeElement, {
      '--side-width': collapsedWidth,
      duration: 0.6,
      ease: 'power2.inOut',
    });

    this.gridTimeline.progress(this.sideExpanded ? 0 : 1);
  }

  private initialiseLogoAnimation(): void {

    if (!this.logo) {
      console.error('Logo element not found');
      return;
    }

    gsap.set(this.logo.nativeElement, {opacity: 1});

    this.logoTimeline = gsap.timeline({paused: true});

    this.logoTimeline.to(this.logo.nativeElement, {
      opacity: -1.5, // 1.5 forces the fade happen in the early part of the timeline
      duration: 0.6,
      ease: 'power2.inOut',
    });

    this.logoTimeline.progress(this.sideExpanded ? 0 : 1);
  }

  private initialiseNavLabelAnimations(): void {

    if (!this.navLabels?.length) {
      console.error('Nav label element(s) not found');
      return;
    }

    const navLabelElements = this.navLabels.map(navLabel => navLabel.nativeElement);

    gsap.set(navLabelElements, {opacity: 1});

    this.navLabelTimeline = gsap.timeline({paused: true});

    this.navLabelTimeline.to(navLabelElements, {
      opacity: -1.5, // 1.5 forces the fade happen in the early part of the timeline
      duration: 0.6,
      ease: 'power2.inOut',
    });

    this.navLabelTimeline.progress(this.sideExpanded ? 0 : 1);
  }

  private initialiseNavGroupTitleAnimations(): void {

    if (!this.navGroupTitles?.length) {
      console.error('Nav group title element(s) not found');
      return;
    }

    const navGroupTitleElements = this.navGroupTitles.map(navGroupTitle => navGroupTitle.nativeElement);

    gsap.set(navGroupTitleElements, {opacity: 1});

    this.navGroupTitleTimeline = gsap.timeline({paused: true});

    this.navGroupTitleTimeline.to(navGroupTitleElements, {
      height: 0,
      opacity: -1.5, // 1.5 forces the fade happen in the early part of the timeline
      duration: 0.6,
      ease: 'power2.inOut',
    });

    this.navGroupTitleTimeline.progress(this.sideExpanded ? 0 : 1);
  }

  private destroyAnimations(): void {

    if (this.gridTimeline) {
      this.gridTimeline.kill();
    }

    if (this.logoTimeline) {
      this.logoTimeline.kill();
    }

    if (this.navLabelTimeline) {
      this.navLabelTimeline.kill();
    }

    if (this.navGroupTitleTimeline) {
      this.navGroupTitleTimeline.kill();
    }
  }

}
